import React, { useEffect, useMemo } from "react";

import {
  CouponType,
  StripeInterval,
  StripeProductVersion,
} from "@intelligems/ig-types";
import { Grid, Skeleton } from "@mui/material";
import { useGetLivePrices } from "@src/api/intelligems/get-live-prices";
import { useGetOrganizationSummary } from "@src/api/organization-summaries/get-organization-summary";
import { PriceBox } from "@src/components/BillingPanel/components/Products/components/PriceBox/PriceBox";

interface ProductsProps {
  billingInterval: StripeInterval;
  coupon: CouponType | null;
}

export const Products = ({ billingInterval, coupon }: ProductsProps) => {
  const adminOrg = useGetOrganizationSummary();
  const livePrices = useGetLivePrices();

  useEffect(() => {
    if (livePrices.isSuccess) {
      const els: HTMLDivElement[] = Array.from(
        document.querySelectorAll(".ig-price-features")
      );
      const maxHeight = Math.max(...els.map((el) => el.clientHeight));
      for (const el of els) {
        el.style.height = `${maxHeight}px`;
      }
    }
  }, [livePrices.isSuccess, billingInterval]);

  const data = useMemo(() => {
    if (!livePrices.isSuccess) {
      return [];
    }

    return livePrices.data.prices.filter((p) => p.interval === billingInterval);
  }, [livePrices.isSuccess, livePrices.data, billingInterval]);

  const currentPlanLevel = useMemo(() => {
    if (adminOrg.isSuccess) {
      return (
        data.find((livePrice) =>
          adminOrg.data.org.stripeSubscriptionSummaries.some((sl) =>
            sl.stripeProductLinks.some(
              // eslint-disable-next-line max-nested-callbacks
              (pl) =>
                pl.stripeProductId === livePrice.product.id &&
                ["active", "trialing"].includes(sl.status)
            )
          )
        )?.product?.level || -1
      );
    }
    return -1;
  }, [adminOrg.isSuccess, adminOrg.data, data]);

  if (!livePrices.isSuccess) {
    return (
      <Grid container item columnGap={"20px"} flexWrap={"nowrap"}>
        <Grid item xs={4} sx={{ height: "380px" }}>
          <Skeleton sx={{ height: "100%" }} />
        </Grid>
        <Grid item xs={4} sx={{ height: "380px" }}>
          <Skeleton sx={{ height: "100%" }} />
        </Grid>
        <Grid item xs={4} sx={{ height: "380px" }}>
          <Skeleton sx={{ height: "100%" }} />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container item flexWrap={"nowrap"} columnGap={"20px"}>
      {data.map((price) => (
        <PriceBox
          price={price}
          key={price.id}
          level={price.product.level || -1}
          currentPlanLevel={currentPlanLevel}
          coupon={coupon}
        />
      ))}
    </Grid>
  );
};
